import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { StaticLink } from '@silvertours/common-landingpages-view';
import Close from '@engineering/icons/close';
import { useTranslations } from 'next-intl';
import { RegionalSettings } from '../../RegionalSettings';
import { Navigation } from './Navigation';
import {
  BackgroundContent,
  StyledRegionalSettingsTrigger,
  Submenu,
  SubmenuCloseButton,
  SubmenuLayer,
} from './MobileNavigationMenu.styles';

type Props = {
  links: StaticLink[];
  navigationLabel: string;
  closeMenu: () => void;
  showRegionalSettings: boolean;
  /** Whether the enclosing mobile menu dialog is open. */
  isOpen: boolean;
};

/**
 * Hamburger menu content: the navigation links plus, on top, the regional
 * settings trigger. Tapping the trigger slides in a submenu that covers the
 * menu and hosts the regional settings form. Picking a currency closes both the
 * submenu and the whole menu; the back button returns to the menu.
 */
const MobileNavigationMenu = ({
  links,
  navigationLabel,
  closeMenu,
  showRegionalSettings,
  isOpen,
}: Props) => {
  const t = useTranslations('features.navigationLegacy.navigation.header');
  const [isSubmenuVisible, setSubmenuVisible] = useState(false);
  const [submenuHeight, setSubmenuHeight] = useState<number>();
  const submenuRef = useRef<HTMLDivElement>(null);

  // Always return to the menu when the whole dialog closes, so reopening starts
  // on the menu rather than a stale submenu.
  useEffect(() => {
    if (!isOpen) {
      setSubmenuVisible(false);
    }
  }, [isOpen]);

  // Grow the menu to the submenu's height when it opens so the covering submenu
  // fits without an internal scrollbar.
  useLayoutEffect(() => {
    if (isSubmenuVisible && submenuRef.current) {
      setSubmenuHeight(submenuRef.current.scrollHeight);
    }
  }, [isSubmenuVisible]);

  // Move focus into the submenu when it opens (CarSPA parity).
  useEffect(() => {
    if (isSubmenuVisible) {
      requestAnimationFrame(() => {
        submenuRef.current
          ?.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          )
          ?.focus();
      });
    }
  }, [isSubmenuVisible]);

  const handleCurrencyChange = () => {
    setSubmenuVisible(false);
    closeMenu();
  };

  return (
    <>
      <BackgroundContent
        inert={isSubmenuVisible}
        style={
          isSubmenuVisible && submenuHeight
            ? { minHeight: submenuHeight }
            : undefined
        }
      >
        {showRegionalSettings && (
          <StyledRegionalSettingsTrigger
            onClick={() => setSubmenuVisible(true)}
            aria-expanded={isSubmenuVisible}
          />
        )}
        <Navigation
          label={navigationLabel}
          links={links}
          mode="mobile"
          onAction={closeMenu}
        />
      </BackgroundContent>

      {showRegionalSettings && (
        <SubmenuLayer aria-hidden={!isSubmenuVisible}>
          <Submenu ref={submenuRef} $isVisible={isSubmenuVisible}>
            <SubmenuCloseButton
              aria-label={t('closeLabel')}
              icon={Close}
              onClick={() => setSubmenuVisible(false)}
            />
            <RegionalSettings onCurrencyChange={handleCurrencyChange} />
          </Submenu>
        </SubmenuLayer>
      )}
    </>
  );
};

export { MobileNavigationMenu };
